import { logout, getUser, isAdmin } from '../../services/auth.js'
import { openCookieSettings } from './CookieBanner.js'
import { FeedbackButton } from './FeedbackButton.js'

const groups = () => [
  ['ETSY WORKSPACE', [['Your listings', '#listings', '▤'], ['Connections', '#shops', '↗']]],
  ['DESIGN TOOLS', [['Mockup generator', '#generator', '◈'], ['Mockup history', '#recent-generations', '◷'], ['Template library', '#mockup-templates', '▦']]],
  ['ACCOUNT', [['Plan & billing', '#pricing', '○'], ...(isAdmin() ? [['Administration', '#admin', '⚙']] : [])]],
]

export const Layout = content => {
  const user = getUser()
  const container = document.createElement('div')
  container.className = 'flex h-screen bg-slate-50'
  const main = document.createElement('main')
  main.id = 'main-content'
  main.tabIndex = -1
  main.className = 'flex-1 min-h-0 min-w-0 flex flex-col overflow-hidden focus:outline-none'
  if (content instanceof Node) main.appendChild(content)
  else if (typeof content === 'string') main.innerHTML = content
  const skip = document.createElement('a')
  skip.href = '#main-content'
  skip.className = 'sr-only focus:not-sr-only focus:absolute focus:z-[100] bg-white p-3'
  skip.textContent = 'Skip to main content'
  skip.onclick = event => { event.preventDefault(); main.focus() }
  container.appendChild(skip)

  if (!user) {
    container.className += ' flex-col'
    const header = document.createElement('header')
    header.className = 'guest-header'
    header.innerHTML = '<a href="/" aria-label="Vizu home"><img src="/images/logo.webp" alt="Vizu.ink" width="120"></a><nav aria-label="Guest navigation"><a href="/">How Vizu works</a><a href="/templates/">Templates</a><a href="#pricing">Pricing</a><a href="#login">Sign in</a><a class="btn-primary" href="#register">Create account</a></nav>'
    const note = document.createElement('div')
    note.className = 'guest-notice'
    note.innerHTML = '<strong>Explore Vizu without connecting a shop.</strong> Create an account to save listing drafts and connect Etsy. Free includes 10 AI listing generations per month. <a href="#register">Get started →</a>'
    container.append(header, note, main)
    const footer = document.createElement('footer')
    footer.className = 'guest-footer'
    footer.innerHTML = '<a href="#privacy-policy">Privacy</a><a href="#terms-of-use">Terms</a><button type="button">Cookie preferences</button>'
    footer.querySelector('button').onclick = openCookieSettings
    container.appendChild(footer)
    return container
  }

  const makeSidebar = () => {
    const sidebar = document.createElement('div')
    sidebar.className = 'workspace-sidebar'
    sidebar.innerHTML = '<a class="workspace-brand" href="#listings"><img src="/images/logo.webp" alt="Vizu.ink" width="140"><span>Etsy listing workspace</span></a>'
    const nav = document.createElement('nav')
    nav.setAttribute('aria-label', 'Workspace navigation')
    for (const [name, links] of groups()) {
      const section = document.createElement('section')
      const heading = document.createElement('h2')
      heading.textContent = name
      section.appendChild(heading)
      for (const [label, href, icon] of links) {
        const link = document.createElement('a')
        link.href = href
        link.className = 'sidebar-link sidebar-link-inactive'
        const symbol = document.createElement('span')
        symbol.textContent = icon
        symbol.setAttribute('aria-hidden', 'true')
        link.append(symbol, document.createTextNode(label))
        section.appendChild(link)
      }
      nav.appendChild(section)
    }
    sidebar.appendChild(nav)
    const account = document.createElement('footer')
    const email = document.createElement('p')
    email.className = 'workspace-email'
    email.textContent = user.email || 'Your account'
    account.appendChild(email)
    const actions = document.createElement('div')
    actions.innerHTML = '<button type="button" data-signout>Sign out</button><button type="button" data-cookies>Cookie preferences</button><a href="#privacy-policy">Privacy</a><a href="#terms-of-use">Terms</a><a href="#home">About Vizu</a>'
    actions.querySelector('[data-cookies]').onclick = openCookieSettings
    actions.querySelector('[data-signout]').onclick = async event => {
      event.currentTarget.disabled = true
      await logout()
      window.location.hash = '#home'
      window.location.reload()
    }
    account.appendChild(actions)
    sidebar.appendChild(account)
    return sidebar
  }
  const desktop = document.createElement('aside')
  desktop.className = 'hidden md:flex flex-shrink-0 w-64'
  desktop.appendChild(makeSidebar())
  const mobile = document.createElement('dialog')
  mobile.className = 'workspace-mobile-menu'
  mobile.setAttribute('aria-label', 'Workspace menu')
  const close = document.createElement('button')
  close.className = 'workspace-menu-close'
  close.textContent = 'Close menu ×'
  close.onclick = () => mobile.close()
  mobile.append(close, makeSidebar())
  mobile.querySelectorAll('a').forEach(link => link.addEventListener('click', () => mobile.close()))
  const wrapper = document.createElement('div')
  wrapper.className = 'flex-1 min-w-0 flex flex-col overflow-hidden'
  const header = document.createElement('header')
  header.className = 'md:hidden flex items-center justify-between bg-white border-b border-slate-200 px-4 py-3'
  header.innerHTML = '<a href="#listings">Vizu · Etsy workspace</a><button type="button" aria-label="Open workspace menu">Menu ☰</button>'
  header.querySelector('button').onclick = () => mobile.showModal()
  wrapper.append(header, main)
  container.append(desktop, mobile, wrapper, FeedbackButton())
  const update = () => {
    const route = (window.location.hash || '#listings').split('?')[0]
    container.querySelectorAll('.sidebar-link').forEach(link => {
      const href = link.getAttribute('href')
      const active = route === href || route.startsWith(`${href}/`)
      link.classList.toggle('sidebar-link-active', active)
      link.classList.toggle('sidebar-link-inactive', !active)
      if (active) link.setAttribute('aria-current', 'page')
      else link.removeAttribute('aria-current')
    })
  }
  window.addEventListener('hashchange', update)
  container.dispose = () => { window.removeEventListener('hashchange', update); if (mobile.open) mobile.close() }
  update()
  return container
}
